import { useEffect, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { Link, useNavigate } from '@tanstack/react-router';
import {
  Badge,
  Button,
  DrawerBody,
  DrawerFooter,
  DrawerHeader,
  ErrorState,
  Separator,
  Skeleton,
  Tabs,
  TabsContent,
  TabsList,
  TabsTrigger,
  Tooltip,
  TooltipContent,
  TooltipTrigger,
  cn,
} from '@vta/ui';
import type { Team } from '@vta/types';
import { apiClient, ApiError } from '../../lib/api-client.ts';
import { useSelectedTeamStore } from '../../stores/selected-team-store.ts';
import {
  formatProgramLabel,
  formatRelativeTime,
  formatTeamNumber,
  programBadgeVariant,
} from '../../lib/format.ts';
import { programMeta } from '../globe/programLegend.tsx';
import { TeamBio } from './TeamBio.tsx';
import { TeamEventsList } from './TeamEventsList.tsx';
import { RelatedTeams } from './RelatedTeams.tsx';

/**
 * Drawer body for the team selection — the presentation surface the camera
 * flight lands on. Renders:
 *   - hero header: program-tinted wash, badges, display team number,
 *     name/org, share (copy-link) action
 *   - location/website summary
 *   - tabs: Events (registration list) / Story (bio) / Related teams
 *   - footer: Open full page · Close
 */
export function TeamDrawerBody({ teamNumber }: { teamNumber: string }) {
  const navigate = useNavigate();
  const close = useSelectedTeamStore((s) => s.close);

  const query = useQuery<{ team: Team } | null>({
    queryKey: ['team', teamNumber],
    queryFn: ({ signal }) =>
      apiClient
        .get<{ team: Team }>(`/api/teams/${encodeURIComponent(teamNumber)}`, { signal })
        .catch((err) => {
          if (err instanceof ApiError && err.status === 404) return null;
          throw err;
        }),
  });

  const team = query.data?.team ?? null;
  const meta = programMeta(team?.program);

  return (
    <>
      <DrawerHeader
        className="relative gap-2 overflow-hidden"
        style={{
          // Program-identity wash — reads as "this team's color", fades to
          // the drawer surface within the header.
          background: `linear-gradient(180deg, color-mix(in srgb, ${meta.cssVar} 14%, transparent), transparent 90%)`,
        }}
      >
        <div className="flex items-center justify-between gap-2">
          <p className="text-2xs uppercase tracking-[0.3em] text-fg-subtle">VEX team</p>
          <ShareLinkButton teamNumber={teamNumber} />
        </div>
        <h2 className="font-mono text-3xl leading-none tracking-tight text-fg">
          {formatTeamNumber(teamNumber)}
        </h2>
        {team ? (
          <div className="flex flex-col gap-1">
            {team.teamName ? <p className="text-base text-fg">{team.teamName}</p> : null}
            {team.organization ? (
              <p className="text-sm text-fg-muted">{team.organization}</p>
            ) : null}
            <div className="mt-1 flex flex-wrap items-center gap-2">
              <Badge variant={programBadgeVariant(team.program)}>
                {formatProgramLabel(team.program, 'short')}
              </Badge>
              {team.gradeLevel ? <Badge variant="neutral">{team.gradeLevel}</Badge> : null}
              <LocationBreadcrumb team={team} />
            </div>
          </div>
        ) : query.isLoading ? (
          <div className="flex flex-col gap-2">
            <Skeleton className="h-5 w-40" />
            <Skeleton className="h-4 w-56" />
          </div>
        ) : null}
      </DrawerHeader>

      <DrawerBody>
        {query.isLoading ? (
          <Loading />
        ) : query.isError ? (
          <ErrorState
            title="Could not load this team"
            description="The team may have been removed or the network dropped."
            retry={() => query.refetch()}
          />
        ) : !team ? (
          <p className="text-sm text-fg-muted">
            Team <span className="font-mono">{teamNumber}</span> isn&apos;t in the atlas yet.
          </p>
        ) : (
          <div className="flex flex-col gap-5">
            <Summary team={team} />
            <Separator />
            <Tabs defaultValue="events">
              <TabsList>
                <TabsTrigger value="events">Events</TabsTrigger>
                <TabsTrigger value="bio">Story</TabsTrigger>
                <TabsTrigger value="related">Related</TabsTrigger>
              </TabsList>
              <TabsContent value="events">
                <TeamEventsList teamNumber={team.teamNumber} />
              </TabsContent>
              <TabsContent value="bio">
                <TeamBio bio={team.bio} card={false} />
              </TabsContent>
              <TabsContent value="related">
                <RelatedTeams
                  teamNumber={team.teamNumber}
                  citySlug={team.citySlug}
                  countrySlug={team.countrySlug}
                />
              </TabsContent>
            </Tabs>
          </div>
        )}
      </DrawerBody>

      <DrawerFooter>
        <div className="flex items-center justify-between gap-2">
          <Button
            variant="primary"
            size="sm"
            onClick={() => {
              close();
              navigate({ to: '/team/$teamNumber', params: { teamNumber } });
            }}
          >
            Open full page
          </Button>
          <Button variant="ghost" size="sm" onClick={close}>
            Close
          </Button>
        </div>
      </DrawerFooter>
    </>
  );
}

function Summary({ team }: { team: Team }) {
  return (
    <dl className="grid grid-cols-2 gap-x-4 gap-y-2 text-xs">
      <Kv label="City" value={team.city ? <CityLink team={team} /> : null} />
      <Kv label="Region" value={team.region} />
      <Kv label="Country" value={team.country ? <CountryLink team={team} /> : null} />
      <Kv label="Grade" value={team.gradeLevel} />
      {team.websiteUrl ? (
        <div className="col-span-2">
          <dt className="text-2xs uppercase tracking-[0.18em] text-fg-subtle">Website</dt>
          <dd>
            <a
              href={team.websiteUrl}
              target="_blank"
              rel="noopener noreferrer"
              className={cn(
                'inline-flex items-center gap-1 text-xs text-accent transition-colors duration-quick hover:text-[rgb(var(--accent-strong))]',
              )}
            >
              {humanizeUrl(team.websiteUrl)}
            </a>
          </dd>
        </div>
      ) : null}
      {team.lastSyncedAt ? (
        <div className="col-span-2">
          <dt className="text-2xs uppercase tracking-[0.18em] text-fg-subtle">Last synced</dt>
          <dd className="text-xs text-fg-muted">{formatRelativeTime(team.lastSyncedAt)}</dd>
        </div>
      ) : null}
    </dl>
  );
}

/**
 * Copy-link action (PRD §7.2.5 shareable views). Copies the canonical
 * `/team/:number` URL; confirms inline for a beat, no toast dependency.
 */
function ShareLinkButton({ teamNumber }: { teamNumber: string }) {
  const [copied, setCopied] = useState(false);

  useEffect(() => {
    if (!copied) return;
    const id = window.setTimeout(() => setCopied(false), 1600);
    return () => window.clearTimeout(id);
  }, [copied]);

  return (
    <Tooltip>
      <TooltipTrigger asChild>
        <button
          type="button"
          onClick={() => {
            const url = `${window.location.origin}/team/${encodeURIComponent(teamNumber)}`;
            void navigator.clipboard?.writeText(url).then(() => setCopied(true));
          }}
          className={cn(
            'inline-flex h-7 w-7 items-center justify-center rounded-md border border-border-subtle text-fg-muted',
            'transition-colors duration-quick hover:border-border-strong hover:text-fg',
            'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-accent',
            copied && 'border-success/60 text-success',
          )}
          aria-label={copied ? 'Link copied' : 'Copy link to this team'}
        >
          {copied ? (
            <svg viewBox="0 0 24 24" className="h-3.5 w-3.5" fill="none" stroke="currentColor" strokeWidth={2} strokeLinecap="round" strokeLinejoin="round" aria-hidden>
              <path d="M20 6 9 17l-5-5" />
            </svg>
          ) : (
            <svg viewBox="0 0 24 24" className="h-3.5 w-3.5" fill="none" stroke="currentColor" strokeWidth={1.5} strokeLinecap="round" strokeLinejoin="round" aria-hidden>
              <path d="M10 13a5 5 0 0 0 7.54.54l3-3a5 5 0 0 0-7.07-7.07l-1.72 1.71" />
              <path d="M14 11a5 5 0 0 0-7.54-.54l-3 3a5 5 0 0 0 7.07 7.07l1.71-1.71" />
            </svg>
          )}
        </button>
      </TooltipTrigger>
      <TooltipContent side="left">{copied ? 'Copied!' : 'Copy link'}</TooltipContent>
    </Tooltip>
  );
}

/** City · Region · Country as a linked breadcrumb (empty parts drop out). */
function LocationBreadcrumb({ team }: { team: Team }) {
  const parts: React.ReactNode[] = [];
  if (team.city) parts.push(<CityLink key="city" team={team} />);
  if (team.region) parts.push(<span key="region" className="text-fg-muted">{team.region}</span>);
  if (team.country) parts.push(<CountryLink key="country" team={team} />);
  if (parts.length === 0) return null;
  return (
    <span className="flex flex-wrap items-center gap-1.5 text-xs">
      {parts.map((node, i) => (
        <span key={i} className="flex items-center gap-1.5">
          {i > 0 ? <span className="text-fg-faint">·</span> : null}
          {node}
        </span>
      ))}
    </span>
  );
}

function Kv({ label, value }: { label: string; value: React.ReactNode }) {
  return (
    <div>
      <dt className="text-2xs uppercase tracking-[0.18em] text-fg-subtle">{label}</dt>
      <dd className="text-xs text-fg-muted">{value || '—'}</dd>
    </div>
  );
}

function CityLink({ team }: { team: Team }) {
  if (!team.citySlug) return <span className="text-fg-muted">{team.city}</span>;
  return (
    <Link
      to="/city/$citySlug"
      params={{ citySlug: team.citySlug }}
      className="text-accent hover:text-[rgb(var(--accent-strong))]"
    >
      {team.city}
    </Link>
  );
}

function CountryLink({ team }: { team: Team }) {
  if (!team.countrySlug) return <span className="text-fg-muted">{team.country}</span>;
  return (
    <Link
      to="/country/$countrySlug"
      params={{ countrySlug: team.countrySlug }}
      className="text-accent hover:text-[rgb(var(--accent-strong))]"
    >
      {team.country}
    </Link>
  );
}

function Loading() {
  return (
    <div className="flex flex-col gap-3">
      <Skeleton className="h-5 w-1/2" />
      <Skeleton className="h-4 w-1/3" />
      <Skeleton className="h-32 w-full" />
      <Skeleton className="h-4 w-2/3" />
      <Skeleton className="h-4 w-1/2" />
    </div>
  );
}

function humanizeUrl(url: string): string {
  try {
    const u = new URL(url);
    return u.host.replace(/^www\./, '') + u.pathname.replace(/\/$/, '');
  } catch {
    return url;
  }
}
